import type { TrailerTypeInput } from '@chukua/common';
import { useEffect, useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { useToast } from '../../../../../shared/hooks/use-toast';
import {
  createTrailerType,
  deactivateTrailerType,
  listTrailerTypes,
  reactivateTrailerType,
  updateTrailerType,
  type TrailerType,
} from '../../../reference-data.api';

export type TrailerTypesState = { status: 'loading' } | { status: 'loaded'; types: TrailerType[] } | { status: 'forbidden' } | { status: 'error' };

function toErrorState(err: unknown): TrailerTypesState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** Same shape as useTruckClasses: create/update keep their error inline, deactivate/reactivate toast. */
export function useTrailerTypes() {
  const [state, setState] = useState<TrailerTypesState>({ status: 'loading' });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;

    listTrailerTypes()
      .then((types) => {
        if (!cancelled) setState({ status: 'loaded', types });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /** Re-fetches after a mutation commits — a plain async function, not tied to the effect above. */
  async function reload(): Promise<void> {
    try {
      setState({ status: 'loaded', types: await listTrailerTypes() });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  async function rowAction(action: () => Promise<void>, successMessage: string, errorMessage: string): Promise<void> {
    try {
      await action();
      await reload();
      toast.success(successMessage);
    } catch {
      toast.error(errorMessage);
    }
  }

  async function save(action: () => Promise<unknown>, successMessage: string): Promise<boolean> {
    setSaveError(null);
    setSaving(true);
    try {
      await action();
      await reload();
      toast.success(successMessage);
      return true;
    } catch (err) {
      setSaveError(
        err instanceof ApiError && err.status === 409 ? 'A trailer type with this code already exists.' : 'Could not save — check the fields and try again.',
      );
      return false;
    } finally {
      setSaving(false);
    }
  }

  const create = (input: TrailerTypeInput) => save(() => createTrailerType(input), `${input.name} created`);
  const update = (id: string, input: TrailerTypeInput) => save(() => updateTrailerType(id, input), `${input.name} updated`);
  const deactivate = (row: TrailerType) => rowAction(() => deactivateTrailerType(row.id), `${row.name} deactivated`, `Couldn't deactivate ${row.name}`);
  const reactivate = (row: TrailerType) => rowAction(() => reactivateTrailerType(row.id), `${row.name} reactivated`, `Couldn't reactivate ${row.name}`);

  return { state, saveError, saving, create, update, resetSaveError: () => setSaveError(null), deactivate, reactivate };
}
